import React, { useState, useEffect, useRef } from 'react'
import { Typography, Paper, Collapse, Box, CircularProgress, TablePagination, ToggleButton, ToggleButtonGroup } from '@mui/material'
import colors from 'theme/colors'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import styles from './safetyEventsBreakdownTable.module.scss'
import { getDriverVehicleBreakdown, DriverVehicleBreakdown, BreakdownRow } from 'api/carrierSafetyEvents'
import strings from 'strings'

type EventSlot = {
  eventType: string
  label: string
  color: string
  count: number
  percentageOfTotal: number
  countPer1k: number
}

type TableRow = {
  id: number
  initials: string
  name: string
  subLabel: string
  miles: number
  events: number
  eventsDelta: number
  per1kMi: number
  eventSlots: EventSlot[]
}

type LegendItem = {
  label: string
  color: string
}

const COLOR_PALETTE = ['#FF8A00', '#0088E1', '#E53935', '#9C27B0', '#4CAF50', '#FF6D00']
const OTHER_COLOR = '#424242'

const formatEventLabel = (eventType: string): string =>
  eventType.split('_').map((w) => w.charAt(0) + w.slice(1).toLowerCase()).join(' ')

const getDriverInitials = (name: string): string => {
  const parts = name.trim().split(' ').filter(Boolean)
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  return name.substring(0, 1).toUpperCase()
}

const getVehicleAbbr = (rowId: string): string => rowId.substring(0, 2).toUpperCase()

const computeTop6Types = (rows: BreakdownRow[]): string[] => {
  const totals: Record<string, number> = {}
  rows.forEach((row) =>
    row.eventsDetail.forEach((d) => {
      if (d.eventType.toUpperCase() === 'OTHER') return
      totals[d.eventType] = (totals[d.eventType] ?? 0) + (d.eventCount ?? 0)
    })
  )
  return Object.entries(totals)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6)
    .map(([type]) => type)
}

const mapRow = (row: BreakdownRow, topTypes: string[], isVehicle: boolean): TableRow => {
  const slotMap: Record<string, { count: number; pct: number; per1k: number }> = {}

  row.eventsDetail.forEach((d) => {
    const key = (d.eventType.toUpperCase() === 'OTHER' || !topTypes.includes(d.eventType)) ? 'OTHER' : d.eventType
    if (!slotMap[key]) slotMap[key] = { count: 0, pct: 0, per1k: 0 }
    slotMap[key].count += d.eventCount ?? 0
    slotMap[key].pct   += d.percentageOfTotal ?? 0
    slotMap[key].per1k += d.countPer1k ?? 0
  })

  const eventSlots: EventSlot[] = topTypes
    .filter((type) => type.toUpperCase() !== 'OTHER')
    .map((type, idx) => ({
      eventType: type,
      label: formatEventLabel(type),
      color: COLOR_PALETTE[idx],
      count: slotMap[type]?.count ?? 0,
      percentageOfTotal: slotMap[type]?.pct ?? 0,
      countPer1k: slotMap[type]?.per1k ?? 0,
    }))

  if (slotMap['OTHER']?.count > 0) {
    eventSlots.push({
      eventType: 'OTHER',
      label: strings.CARRIERS.BREAKDOWN_TABLE.OTHER_LABEL,
      color: OTHER_COLOR,
      count: slotMap['OTHER'].count,
      percentageOfTotal: slotMap['OTHER'].pct,
      countPer1k: slotMap['OTHER'].per1k,
    })
  }

  return {
    id: row.id,
    initials: isVehicle ? getVehicleAbbr(row.rowId) : getDriverInitials(row.rowName),
    name: row.rowName,
    subLabel: row.rowId,
    miles: row.miles ?? 0,
    events: row.events ?? 0,
    eventsDelta: row.eventsDiff ?? 0,
    per1kMi: row.eventsPer1k ?? 0,
    eventSlots,
  }
}

const buildLegend = (topTypes: string[], hasOther: boolean): LegendItem[] => {
  const items = topTypes
    .filter((type) => type.toUpperCase() !== 'OTHER')
    .map((type, idx) => ({ label: formatEventLabel(type), color: COLOR_PALETTE[idx] }))
  if (hasOther) items.push({ label: strings.CARRIERS.BREAKDOWN_TABLE.OTHER_LABEL, color: OTHER_COLOR })
  return items
}

const AVATAR_COLORS = ['#0088E1', '#FF8A00', '#4CAF50', '#9C27B0', '#E53935', '#00BCD4', '#FF5722', '#607D8B']

const CompositionBar = ({ slots, total }: { slots: EventSlot[]; total: number }) => (
  <div className={styles.compositionBar}>
    {slots.map((slot) => {
      const pct = total > 0 ? (slot.count / total) * 100 : 0
      if (pct === 0) return null
      return (
        <div
          key={slot.eventType}
          className={styles.compositionSegment}
          style={{ width: `${pct}%`, backgroundColor: slot.color }}
          title={`${slot.label}: ${slot.count} (${pct.toFixed(1)}%)`}
        />
      )
    })}
  </div>
)

const ExpandedEventCards = ({ row, useKilometers }: { row: TableRow; useKilometers: boolean }) => (
  <div className={styles.expandedCards}>
    {row.eventSlots.map((slot) => (
      <div key={slot.eventType} className={styles.eventCard}>
        <div className={styles.eventCardHeader}>
          <span className={styles.eventDot} style={{ backgroundColor: slot.color }} />
          <Typography variant="caption" color="text.secondary">{slot.label}</Typography>
        </div>
        <div className={styles.eventCardBody}>
          <Typography variant="h5" fontWeight={600}>{slot.count ?? 0}</Typography>
          <Typography variant="caption" color="text.secondary">{strings.CARRIERS.BREAKDOWN_TABLE.PCT_OF_TOTAL((slot.percentageOfTotal ?? 0).toFixed(1))}</Typography>
        </div>
        <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
          {(slot.countPer1k ?? 0).toFixed(2)} {useKilometers ? strings.CARRIERS.BREAKDOWN_TABLE.PER_1K_KM : strings.CARRIERS.BREAKDOWN_TABLE.PER_1K_MI}
        </Typography>
        <div className={styles.eventCardBar} style={{ backgroundColor: slot.color }} />
      </div>
    ))}
  </div>
)

type SortField = 'name' | 'miles' | 'events' | 'per1kMi'
type SortDir   = 'asc' | 'desc'

const sortRows = (rows: TableRow[], field: SortField | null, dir: SortDir): TableRow[] => {
  if (!field) return rows
  const sorted = [...rows].sort((a, b) => {
    const av = field === 'name' ? a.name : a[field]
    const bv = field === 'name' ? b.name : b[field]
    if (typeof av === 'string' && typeof bv === 'string') return av.localeCompare(bv)
    return (av as number) - (bv as number)
  })
  return dir === 'desc' ? sorted.reverse() : sorted
}

const SortIcon = ({ field, active, dir }: { field: SortField; active: SortField | null; dir: SortDir }) => {
  if (field !== active) return <UnfoldMoreIcon sx={{ fontSize: 14, ml: 0.5, color: '#bbb' }} />
  return dir === 'asc'
    ? <ArrowUpwardIcon   sx={{ fontSize: 13, ml: 0.5 }} />
    : <ArrowDownwardIcon sx={{ fontSize: 13, ml: 0.5 }} />
}

const SortableHeader = ({
  field, className, active, dir, onSort, children,
}: {
  field: SortField
  className: string
  active: SortField | null
  dir: SortDir
  onSort: (f: SortField) => void
  children: React.ReactNode
}) => (
  <div
    className={className}
    role="columnheader"
    tabIndex={0}
    aria-sort={field === active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    onClick={() => onSort(field)}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSort(field) } }}
  >
    {children}
  </div>
)

interface SafetyEventsBreakdownTableProps {
  carrierId: string
  timeframe?: string
  selectedEventTypes?: string[]
  refreshKey?: number
  useKilometers?: boolean
  onPdfDataReady?: (
    driverData: string[][],
    driverColumns: string[],
    vehicleData: string[][],
    vehicleColumns: string[]
  ) => void
}

const SafetyEventsBreakdownTable: React.FC<SafetyEventsBreakdownTableProps> = ({
  carrierId,
  timeframe = '30',
  selectedEventTypes = [],
  refreshKey = 0,
  useKilometers = false,
  onPdfDataReady,
}) => {
  const ROWS_PER_PAGE_OPTIONS = [5, 10, 20, 50, 100]

  const [view, setView] = useState<'driver' | 'vehicle'>('vehicle')
  const [expandedRow, setExpandedRow] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [driverRows, setDriverRows] = useState<TableRow[]>([])
  const [vehicleRows, setVehicleRows] = useState<TableRow[]>([])
  const [driverLegend, setDriverLegend] = useState<LegendItem[]>([])
  const [vehicleLegend, setVehicleLegend] = useState<LegendItem[]>([])
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(100)
  const [sortField, setSortField] = useState<SortField | null>(null)
  const [sortDir, setSortDir]     = useState<SortDir>('desc')
  const scrollRef = useRef<HTMLDivElement>(null)

  const selectedEventTypesKey = selectedEventTypes.join(',')

  useEffect(() => {
    if (!carrierId || !selectedEventTypesKey) {
      setIsLoading(false)
      setDriverRows([])
      setVehicleRows([])
      setDriverLegend([])
      setVehicleLegend([])
      setExpandedRow(null)
      setPage(0)
      return
    }

    const eventTypes = selectedEventTypesKey.split(',')

    const endDate = new Date()
    endDate.setHours(23, 59, 59, 0)
    const startDate = new Date()
    startDate.setDate(startDate.getDate() - parseInt(timeframe))
    startDate.setHours(0, 0, 0, 0)

    const pad = (n: number) => String(n).padStart(2, '0')
    const fmt = (d: Date) =>
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`

    setIsLoading(true)
    setExpandedRow(null)
    setPage(0)

    getDriverVehicleBreakdown(carrierId, eventTypes, fmt(startDate), fmt(endDate), useKilometers)
      .then((data: DriverVehicleBreakdown) => {
        const topDriverTypes  = computeTop6Types(data.breakdownByDriver)
        const topVehicleTypes = computeTop6Types(data.breakdownByVehicle)

        const mappedDrivers  = data.breakdownByDriver.map((r)  => mapRow(r, topDriverTypes, false))
        const mappedVehicles = data.breakdownByVehicle.map((r) => mapRow(r, topVehicleTypes, true))

        const driversHaveOther  = mappedDrivers.some((r)  => r.eventSlots.some((s) => s.eventType === 'OTHER'))
        const vehiclesHaveOther = mappedVehicles.some((r) => r.eventSlots.some((s) => s.eventType === 'OTHER'))

        setDriverRows(mappedDrivers)
        setVehicleRows(mappedVehicles)
        setDriverLegend(buildLegend(topDriverTypes, driversHaveOther))
        setVehicleLegend(buildLegend(topVehicleTypes, vehiclesHaveOther))

        if (onPdfDataReady) {
          const distLabel = useKilometers ? 'KM' : 'Mi'
          const formatDelta = (d: number) => (d > 0 ? `+${d}` : `${d}`)
          const formatBreakdown = (slots: EventSlot[]) =>
            slots
              .filter((s) => s.count > 0)
              .map((s) => `${s.label}: ${s.count} (${s.percentageOfTotal.toFixed(1)}%)`)
              .join('\n') || '—'

          const byPer1kDesc = (a: TableRow, b: TableRow) => b.per1kMi - a.per1kMi

          const driverColumns = ['Driver', 'Miles', 'Events', 'Events Change', `Per 1k ${distLabel}`, 'Event Breakdown']
          const driverData = [...mappedDrivers].sort(byPer1kDesc).map((row) => [
            row.name,
            row.miles.toLocaleString(),
            String(row.events),
            formatDelta(row.eventsDelta),
            row.per1kMi.toFixed(2),
            formatBreakdown(row.eventSlots),
          ])

          const vehicleColumns = ['Vehicle', 'Vehicle ID', 'Miles', 'Events', 'Events Change', `Per 1k ${distLabel}`, 'Event Breakdown']
          const vehicleData = [...mappedVehicles].sort(byPer1kDesc).map((row) => [
            row.name,
            row.subLabel,
            row.miles.toLocaleString(),
            String(row.events),
            formatDelta(row.eventsDelta),
            row.per1kMi.toFixed(2),
            formatBreakdown(row.eventSlots),
          ])

          onPdfDataReady(driverData, driverColumns, vehicleData, vehicleColumns)
        }
      })
      .catch(() => {
        setDriverRows([])
        setVehicleRows([])
        setDriverLegend([])
        setVehicleLegend([])
      })
      .finally(() => setIsLoading(false))
  }, [carrierId, timeframe, selectedEventTypesKey, refreshKey, useKilometers])

  const toggleRow = (id: number) => setExpandedRow((prev) => (prev === id ? null : id))

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortField(field)
      setSortDir('asc')
    }
    setPage(0)
    setExpandedRow(null)
  }

  const baseRows = view === 'driver' ? driverRows : vehicleRows
  const rows     = sortRows(baseRows, sortField, sortDir)
  const legend       = view === 'driver' ? driverLegend : vehicleLegend
  const firstColLabel = view === 'driver' ? strings.CARRIERS.BREAKDOWN_TABLE.COL_DRIVER : strings.CARRIERS.BREAKDOWN_TABLE.COL_VEHICLE
  const pageStart     = page * rowsPerPage
  const paginatedRows = rows.slice(pageStart, pageStart + rowsPerPage)

  return (
    <Paper elevation={0} sx={{ border: '1px solid #E9E9E9', borderRadius: '14px', overflow: 'hidden' }}>
      <div className={styles.tableHeader}>
        <div>
          <Typography fontSize="1rem" fontWeight={500} color="#333">{strings.CARRIERS.BREAKDOWN_TABLE.TITLE}</Typography>
          <Typography variant="caption" color="text.secondary">
            {strings.CARRIERS.BREAKDOWN_TABLE.SUBTITLE(timeframe)}
          </Typography>
          {!isLoading && legend.length > 0 && (
            <div className={styles.legend}>
              {legend.map((item) => (
                <div key={item.label} className={styles.legendItem}>
                  <span className={styles.legendDot} style={{ backgroundColor: item.color }} />
                  <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                </div>
              ))}
            </div>
          )}
        </div>
        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={(_, newView) => {
            if (!newView) return
            setView(newView)
            setExpandedRow(null)
            setPage(0)
            setSortField(null)
            setSortDir('desc')
            if (scrollRef.current) scrollRef.current.scrollTop = 0
          }}
          sx={{
            flexShrink: 0,
            backgroundColor: colors.platinum,
            borderRadius: '6px',
            padding: '1px',
            border: `1px solid ${colors.silver}`,
            '& .MuiToggleButtonGroup-grouped': {
              border: 0,
              borderRadius: '4px',
              px: 1.5,
              py: 0.5,
              fontSize: '0.8125rem',
              fontWeight: 600,
              textTransform: 'none',
              '&.Mui-selected': {
                backgroundColor: colors.white,
                color: colors.primary,
                boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                '&:hover': { backgroundColor: colors.white, color: colors.primary },
              },
              '&:not(.Mui-selected)': {
                color: colors.grey,
                backgroundColor: 'transparent',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)', color: colors.primary },
              },
            },
          }}
        >
          <ToggleButton value="vehicle">{strings.CARRIERS.BREAKDOWN_TABLE.BY_VEHICLE}</ToggleButton>
          <ToggleButton value="driver">{strings.CARRIERS.BREAKDOWN_TABLE.BY_DRIVER}</ToggleButton>
        </ToggleButtonGroup>
      </div>

      {!isLoading && rows.length > 0 && <div className={styles.columnHeaders}>
        <SortableHeader field="name" className={`${styles.colDriver} ${styles.sortable}`} active={sortField} dir={sortDir} onSort={handleSort}>
          {firstColLabel} <SortIcon field="name" active={sortField} dir={sortDir} />
        </SortableHeader>
        <SortableHeader field="miles" className={`${styles.colMiles} ${styles.sortable}`} active={sortField} dir={sortDir} onSort={handleSort}>
          {useKilometers ? strings.CARRIERS.BREAKDOWN_TABLE.COL_KILOMETERS : strings.CARRIERS.BREAKDOWN_TABLE.COL_MILES} <SortIcon field="miles" active={sortField} dir={sortDir} />
        </SortableHeader>
        <SortableHeader field="events" className={`${styles.colEvents} ${styles.sortable}`} active={sortField} dir={sortDir} onSort={handleSort}>
          {strings.CARRIERS.BREAKDOWN_TABLE.COL_EVENTS} <SortIcon field="events" active={sortField} dir={sortDir} />
        </SortableHeader>
        <SortableHeader field="per1kMi" className={`${styles.colPer1k} ${styles.sortable}`} active={sortField} dir={sortDir} onSort={handleSort}>
          {useKilometers ? strings.CARRIERS.BREAKDOWN_TABLE.COL_PER_1K_KM : strings.CARRIERS.BREAKDOWN_TABLE.COL_PER_1K_MI} <SortIcon field="per1kMi" active={sortField} dir={sortDir} />
        </SortableHeader>
        <div className={styles.colComposition}>{strings.CARRIERS.BREAKDOWN_TABLE.COL_EVENT_COMPOSITION}</div>
      </div>}

      <div className={styles.rowsScroll} ref={scrollRef}>
      {isLoading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 12 }}>
          <CircularProgress size={40} sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary">{strings.CARRIERS.BREAKDOWN_TABLE.LOADING}</Typography>
        </Box>
      ) : rows.length === 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 12 }}>
          <Typography variant="body2" color="text.secondary" fontWeight={500}>{strings.CARRIERS.BREAKDOWN_TABLE.NO_EVENTS}</Typography>
          <Typography variant="caption" color="text.secondary" mt={0.5}>{strings.CARRIERS.BREAKDOWN_TABLE.NO_EVENTS_HINT}</Typography>
        </Box>
      ) : (
        paginatedRows.map((row, idx) => {
          const isExpanded  = expandedRow === row.id
          const globalIdx   = pageStart + idx
          const avatarColor = expandedRow === null || isExpanded ? AVATAR_COLORS[globalIdx % AVATAR_COLORS.length] : '#BDBDBD'
          const deltaPositive = row.eventsDelta > 0
          const deltaZero     = row.eventsDelta === 0

          return (
            <div key={row.id} className={styles.rowWrapper}>
              <div
                className={`${styles.tableRow} ${isExpanded ? styles.tableRowExpanded : ''}`}
                role="button"
                tabIndex={0}
                aria-expanded={isExpanded}
                onClick={() => toggleRow(row.id)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleRow(row.id) } }}
              >
                <div className={styles.colDriver}>
                  <div className={styles.avatar} style={{ backgroundColor: avatarColor }}>
                    {view === 'vehicle'
                      ? <LocalShippingIcon sx={{ fontSize: 18 }} />
                      : row.initials}
                  </div>
                  <div>
                    <Typography variant="body2" fontWeight={600}>{row.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{row.subLabel}</Typography>
                  </div>
                </div>

                <div className={styles.colMiles}>
                  <Typography variant="body2">{Math.round(row.miles).toLocaleString()}</Typography>
                </div>

                <div className={styles.colEvents}>
                  <Typography variant="body2" fontWeight={600} component="span">{row.events}</Typography>
                  {!deltaZero && (
                    <Typography
                      variant="caption"
                      component="span"
                      sx={{ ml: 0.5, color: deltaPositive ? '#E53935' : '#4CAF50', fontWeight: 600 }}
                    >
                      {deltaPositive ? `+${row.eventsDelta}` : row.eventsDelta}
                    </Typography>
                  )}
                </div>

                <div className={styles.colPer1k}>
                  <Typography variant="body2" fontWeight={600}>{(row.per1kMi ?? 0).toFixed(2)}</Typography>
                </div>

                <div className={styles.colComposition}>
                  <CompositionBar slots={row.eventSlots} total={row.events} />
                </div>

                <div className={styles.expandIcon}>
                  {isExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                </div>
              </div>

              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <div className={styles.expandedRow}>
                  <ExpandedEventCards row={row} useKilometers={useKilometers} />
                </div>
              </Collapse>
            </div>
          )
        })
      )}
      </div>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid #E9E9E9', px: '20px' }}>
        <Typography variant="caption" color="text.secondary">{strings.CARRIERS.BREAKDOWN_TABLE.TREND_LABEL(timeframe)}</Typography>
        {!isLoading && rows.length > 0 && (
          <TablePagination
            component="div"
            count={rows.length}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            onPageChange={(_e, newPage) => { setPage(newPage); setExpandedRow(null) }}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); setExpandedRow(null) }}
            sx={{ border: 'none', '.MuiTablePagination-toolbar': { minHeight: 44, pl: 0 } }}
          />
        )}
      </Box>
    </Paper>
  )
}

export default SafetyEventsBreakdownTable
